import React from "react";
import { Link } from "react-router-dom";
import { LegalShell, Section, ValuePillars, ResponsiveTable } from "../components/LegalShell";

const LAST_UPDATED = "26 February 2026";

export default function About() {
    return (
        <LegalShell
            eyebrow="Company"
            title="About EuroLaw Hub"
            subtitle="EU legal research workspace for qualified counsel, grounded in primary sources, transparent about its limitations, designed for production work."
            lastUpdated={LAST_UPDATED}
        >
            <Section title="Our mission">
                <p>
                    EuroLaw Hub exists because the volume and pace of EU regulation
                    have outgrown the tools available to qualified counsel. From the
                    Digital Services Act and the AI Act to NIS2, DORA, CSRD and the
                    Data Act, in-house teams and law firms are being asked to advise
                    on instruments that are still being interpreted in real time.
                </p>
                <p>
                    Our mission is to give serious EU legal professionals a single
                    decision-support environment that is fast enough for daily use,
                    careful enough for partner review, and transparent about exactly
                    where every answer comes from.
                </p>
            </Section>

            <ValuePillars items={[
                {
                    label: "Pillar 01",
                    title: "Precision",
                    body: (
                        "Every research module is built to surface primary EU sources " +
                        "first: EUR-Lex, curia.europa.eu, the supervisory authorities, " +
                        "treaty articles. The model's job is to organise and explain, " +
                        "not to replace the source."
                    ),
                },
                {
                    label: "Pillar 02",
                    title: "Transparency",
                    body: (
                        "We tell you what the system is doing. Citations appear inline. " +
                        "Risk classifications expose the question tree. AI-generated " +
                        "bulletins are labelled. Our model card and DPA are public."
                    ),
                },
                {
                    label: "Pillar 03",
                    title: "Compliance",
                    body: (
                        "Built to GDPR, AI Act and DORA expectations. No training on " +
                        "user content. Encryption in transit and at rest. Role-based " +
                        "access. Breach response procedure aligned with Art. 33 GDPR."
                    ),
                },
            ]} />

            <Section title="What EuroLaw Hub is, and what it is not">
                <p>
                    EuroLaw Hub is a research and compliance workspace. The runtime
                    model is Anthropic Claude, accessed only from our secure backend.
                    The product covers the EU regulatory perimeter that matters most
                    to qualified counsel today: data and privacy law, AI and digital
                    rules, financial services, cyber resilience, sustainability, and
                    constitutional / Charter matters.
                </p>
                <p>
                    EuroLaw Hub is not a replacement for qualified legal advice. It
                    is decision-support tooling that augments the judgement of a
                    qualified lawyer. The user remains responsible for verifying the
                    output against the primary sources and applying it to a specific
                    matter.
                </p>
            </Section>

            <Section title="Get in touch">
                <p>
                    For partnerships, media, careers and general enquiries:
                </p>
                <p>
                    <a className="underline" href="mailto:altay@eurolawhub.com">
                        altay@eurolawhub.com
                    </a>
                </p>
                <p>
                    For account-specific questions you can also use the support
                    chatbot at the bottom-right of any page, or open the{" "}
                    <Link to="/security" className="underline">Security</Link>,{" "}
                    <Link to="/privacy" className="underline">Privacy</Link> and{" "}
                    <Link to="/transparency" className="underline">Transparency</Link>{" "}
                    pages for a full picture of how the platform operates.
                </p>
            </Section>
        </LegalShell>
    );
}
